import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExperimentsApiClient } from '../api/experimentsClient';
import type {
  ExperimentDocument,
  ExperimentInputPayload,
  ExperimentTransitionPayload,
} from '../experimentTypes';

interface UseExperimentsInput {
  projectId: string;
  enabled: boolean;
}

interface UseExperimentsResult {
  experiments: ExperimentDocument[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createExperiment: (input: ExperimentInputPayload) => Promise<ExperimentDocument>;
  updateExperiment: (id: string, input: Partial<ExperimentInputPayload>) => Promise<ExperimentDocument>;
  transitionExperiment: (id: string, payload: ExperimentTransitionPayload) => Promise<ExperimentDocument>;
}

export function useExperiments(input: UseExperimentsInput): UseExperimentsResult {
  const [experiments, setExperiments] = useState<ExperimentDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const client = useMemo(() => new ExperimentsApiClient(), []);

  useEffect(() => {
    if (!input.enabled || !input.projectId) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    client.list(input.projectId, controller.signal)
      .then(setExperiments)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load experiments');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [client, input.enabled, input.projectId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  const createExperiment = useCallback(async (payload: ExperimentInputPayload) => {
    const experiment = await client.create(input.projectId, payload);
    refresh();
    return experiment;
  }, [client, input.projectId, refresh]);

  const updateExperiment = useCallback(async (id: string, payload: Partial<ExperimentInputPayload>) => {
    const experiment = await client.update(input.projectId, id, payload);
    refresh();
    return experiment;
  }, [client, input.projectId, refresh]);

  const transitionExperiment = useCallback(async (id: string, payload: ExperimentTransitionPayload) => {
    const experiment = await client.transition(input.projectId, id, payload);
    refresh();
    return experiment;
  }, [client, input.projectId, refresh]);

  return { experiments, loading, error, refresh, createExperiment, updateExperiment, transitionExperiment };
}
